import React, { useState, useEffect } from "react";
import {
  SearchBarTable,
  Pagination,
  Button,
  DeleteModal,
} from "../../../components";
import {
  FaReceipt,
  FaAngleDoubleUp,
  FaEdit,
  FaTrash,
  FaPlus,
  FaCreditCard,
} from "react-icons/fa";
import { BsThreeDotsVertical } from "react-icons/bs";
import { RiUpload2Fill } from "react-icons/ri";
import { Link } from "react-router-dom";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import {
  fetchHouseholds,
  deleteHousehold,
} from "../../../store/slices/tablesInfoSlices/houseHoldsSlice";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

const TableHouseHold = () => {
  const [searchInput, setSearchInput] = useState("");
  const dispatch = useDispatch();
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const households = useSelector((state) => state.households.data);
  const isLoading = useSelector((state) => state.households.isLoading);
  const initialFilteredData = Array.isArray(households) ? households : [];
  const [filteredData, setFilteredData] = useState(initialFilteredData);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const itemsPerPageOptions = [10, 25, 50, 100];

  useEffect(() => {
    dispatch(fetchHouseholds());
  }, [dispatch]);

  const [searchOptions] = useState([
    "Filtro",
    "Filtro sipas emrit",
    "Filtro sipas mbiemrit",
  ]);

  const handleItemsPerPageChange = (e) => {
    const newItemsPerPage = parseInt(e.target.value, 10);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleSearchInputChange = (e) => {
    setSearchInput(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    filterTableData(searchInput);
  };

  const filterTableData = (searchText, option) => {
    let filtered = [];

    if (Array.isArray(households)) {
      filtered = households.filter((item) => {
        const {
          name,
          surname,
          parent_name,
          personal_number,
          address,
          door,
          parcel_number,
          zone,
          surface,
          status,
          household_id,
        } = item;
        const fullName = `${name} ${surname}`;
        return (
          fullName.toLowerCase().includes(searchText.toLowerCase()) ||
          name?.toLowerCase().includes(searchText.toLowerCase()) ||
          surname?.toLowerCase().includes(searchText.toLowerCase()) ||
          parent_name?.toLowerCase().includes(searchText.toLowerCase()) ||
          personal_number?.toLowerCase().includes(searchText.toLowerCase()) ||
          address?.toLowerCase().includes(searchText.toLowerCase()) ||
          door?.toLowerCase().includes(searchText.toLowerCase()) ||
          parcel_number?.toLowerCase().includes(searchText.toLowerCase()) ||
          zone?.toLowerCase().includes(searchText.toLowerCase()) ||
          status?.toLowerCase().includes(searchText.toLowerCase())
        );
      });
    }
    setFilteredData(filtered);
  };

  useEffect(() => {
    filterTableData(searchInput);
  }, [searchInput]);

  const totalItems = filteredData?.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setDeleteModalOpen(true);
  };

  const handleDelete = () => {
    if (itemToDelete && itemToDelete.id) {
      dispatch(deleteHousehold(itemToDelete.id));
      setDeleteModalOpen(false);
      setItemToDelete(null);
    }
  };

  const handleExportToExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("MenaxhimiAmvisrive");

    const customHeaders = [
      "Nr i Klientit",
      "Emri",
      "Mbiemri",
      "Emri i Prindit",
      "Nr.Personal",
      "Adresa",
      "Dera",
      "Nr.Parcela",
      "Zona",
      "Sipërfaqja",
      "Status",
    ];

    const headerRow = worksheet.addRow(customHeaders);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "D0D5DD" },
      };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });
    const dataColumn = worksheet.getColumn(7);
    dataColumn.width = 25;
    const emriColumn = worksheet.getColumn(2);
    emriColumn.width = 20;
    filteredData.forEach(
      ({
        id,
        name,
        surname,
        parent_name,
        personal_number,
        address,
        door,
        parcel_number,
        zone,
        surface,
        status,
      }) => {
        worksheet.addRow([
          id,
          name,
          surname,
          parent_name,
          personal_number,
          address,
          door,
          parcel_number,
          zone,
          surface,
          status,
        ]);
      }
    );
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, "exportTable.xlsx");
  };

  useEffect(() => {
    if (households) {
      filterTableData(searchInput);
    }
  }, [households, searchInput]);

  const { t } = useTranslation();

  return (
    <>
      <div className="p-3 mt-3">
        <div className="flex items-center justify-between pb-4 ">
          <h1 className="text-blue-900 font-semibold	">{t("householdTitle")}</h1>

          <div className="flex items-center justify-end bg-white dark:bg-gray-900">
            <SearchBarTable
              searchInput={searchInput}
              handleSearchInputChange={handleSearchInputChange}
              handleSearchSubmit={handleSearchSubmit}
              placeholder={t("search")}
            />

            <select
              id="itemsPerPage"
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              className="p-[6px] ml-2 text-xs border text-gray-400 rounded-md outline-none focus:ring focus:ring-blue-300"
            >
              {itemsPerPageOptions?.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <Button
              className="text-blue-800 text-sm bg-gray-100"
              onClick={handleExportToExcel}
            >
              <RiUpload2Fill className="mx-2" />
            </Button>
            <Link to="/add-new-citizen">
              <Button className="text-white bg-blue-800 transition duration-300 ease-in-out hover:bg-gray-200 hover:text-blue-800">
                <FaPlus className="mr-1" /> {t("addNewCitizen")}
              </Button>
            </Link>
          </div>
        </div>
        <div
          className="relative overflow-x-auto shadow-md sm:rounded-lg"
          style={{ borderRadius: 1 }}
        >
          <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400 whitespace-nowrap	">
            <thead className="text-xs text-blue-800 font-light whitespace-nowrap	bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
              <tr>
                <th scope="col" className="px-6 py-3">
                  {t("clientNumber")}
                </th>
                <th scope="col" className="px-6 py-3">
                  {t("name")}
                </th>
                <th scope="col" className="px-6 py-3">
                  {t("surname")}
                </th>
                <th scope="col" className="px-6 py-3">
                  {t("parentName")}
                </th>
                <th scope="col" className="px-6 py-3">
                  {t("personalNumber")}
                </th>
                <th scope="col" className="px-6 py-3">
                  {t("address")}
                </th>
                <th scope="col" className="px-6 py-3">
                  {t("door")}
                </th>
                <th scope="col" className="px-6 py-3">
                  {t("parcelNumber")}
                </th>
                <th scope="col" className="px-6 py-3">
                  {t("zone")}
                </th>
                <th scope="col" className="px-6 py-3">
                  {t("surface")}
                </th>
                <th scope="col" className="px-6 py-3">
                  {t("status")}
                </th>
                <th scope="col" className="px-1 py-3"></th>
              </tr>
            </thead>
            {isLoading ? (
              <p>Is loading</p>
            ) : (
              <tbody className="text-xs">
                {currentItems.length === 0 ? (
                  <tr>
                    <td colSpan="12" className="text-center py-4">
                      No data found
                    </td>
                  </tr>
                ) : (
                  currentItems.map((item) => (
                    <tr key={item.id} className="border-b">
                      <td className="px-6 py-4">{item.id}</td>
                      <td className="px-6 py-4">{item.name}</td>
                      <td className="px-6 py-4">{item.surname}</td>
                      <td className="px-6 py-4">{item.parent_name}</td>
                      <td className="px-6 py-4">{item.personal_number}</td>
                      <td className="px-6 py-4">{item.address}</td>
                      <td className="px-6 py-4">{item.door}</td>
                      <td className="px-6 py-4">{item.parcel_number}</td>
                      <td className="px-6 py-4">{item.zone}</td>
                      <td className="px-6 py-4">{item.surface}</td>
                      <td className="px-6 py-4">{item.status}</td>
                      <td className="py-4 flex gap-2 cursor-pointer text-gray-400">
                        <Link to={`/household/${item.id}`}>
                          <div title="Fatura">
                            <FaReceipt className="w-[17px] h-[17px]" />
                          </div>
                        </Link>
                        <Link to={`/certificates/${item.id}`}>
                          <div title="Vertetimi">
                            <FaAngleDoubleUp className="w-[17px] h-[17px]" />
                          </div>
                        </Link>
                        <Link
                          to={`/financial-card/${item.personal_number}/${item.name}/${item.surname}`}
                        >
                          <div title="Karta Financiare">
                            <FaCreditCard className="w-[17px] h-[17px]" />
                          </div>
                        </Link>
                        <Link to={`/edit-citizen/${item.id}`}>
                          <div title="Edit">
                            <FaEdit className="w-[17px] h-[17px]" />
                          </div>
                        </Link>
                        <div
                          title="Delete"
                          onClick={() => handleDeleteClick(item)}
                        >
                          <FaTrash className="w-[17px] h-[17px]" />
                        </div>
                        <Link to={`/citizen-info/${item.id}`}>
                          <div title="Info">
                            <BsThreeDotsVertical className="w-[17px] h-[17px]" />
                          </div>
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            )}
          </table>
        </div>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          handlePageChange={handlePageChange}
          className="flex items-center justify-center mt-4"
        />
      </div>
      <DeleteModal
        isOpen={isDeleteModalOpen}
        closeModal={() => setDeleteModalOpen(false)}
        handleDelete={handleDelete}
        question="Deshironi ta fshini amviserine ?"
        actionTitle="Delete"
      />
    </>
  );
};

export default TableHouseHold;
